import Vue from 'vue';
import {mapGetters} from 'vuex';
import Component, {mixins} from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';

import {
  AuthService,
  EventService,
  OrderService,
  PaymentMethodService,
  PrivacyStatementService,
  ProductService,
  SlotService,
  TermsAndConditionsService,
} from '../../../Services';
import {
  Event, OpeningTime,
  Order,
  OrderItem,
  PaymentMethod,
  PrivacyStatement,
  Product,
  ProductGroup,
  Slot, TermsAndConditions,
  TicketConfiguration,
  TicketConfigurationCollection,
} from '../../../Models';
import {CartDTO, CustomerDataDTO, OrderDTO, SessionDTO, TicketConfigurationDTO} from '../../../DTOs';
import {DELETE_CART, SET_REGISTRATION_CODES, UPDATE_CART, UPDATE_PAYMENT_METHODS} from '../../../store/commits';
import TicketConfigurationItem from '../../Components/TicketList/TicketConfigurationItem/TicketConfigurationItem.vue';
import CustomerForm from '../../Checkout/CustomerForm/CustomerForm.vue';
import TicketConfigurationCollectionItem from '../../Components/TicketList/TicketConfigurationCollectionItem/TicketConfigurationCollectionItem.vue';
import HeaderIcons from '../../Components/HeaderIcons/HeaderIcons.vue';
import IntoCart from '../../Icons/IntoCart.vue';
import Check from '../../Icons/Check.vue';
import Arrow from '../../Icons/Arrow.vue';
import Modal from '../../Modal/Modal.vue';
import {ApiErrorHandler} from '../../../Errors/ApiErrorHandler';
import {BaseMixin} from '../../../Mixins';
import {PaymentTypeEnum, PublicLinks} from '../../../Enums';
import {OpeningTimeService} from '../../../Services/OpeningTimeService';
import {OpenPeriod} from '../../../Models/OpenPeriod';
// @ts-ignore
import {ProductGroupService} from '../../../Services/ProductGroupService';

@Component({
  computed: {
    ...mapGetters({
      currentOrder: 'getCurrentOrder',
      cart: 'getCart',
      userLanguage: 'getUserLanguage',
      sessionDTO: 'getSession',
      currentBuyerData: 'getBuyerData',
      storageProducts: 'getProducts',
      storageEvents: 'getEvents',
    }),
  },
  components: {
    CustomerForm,
    IntoCart,
    Check,
    Arrow,
    Modal,
    TicketConfigurationItem,
    TicketConfigurationCollectionItem,
    HeaderIcons,
  },
})
export default class TicketListView extends mixins(BaseMixin) {
  @Prop({type: String, required: true}) inventoryUrl: string | undefined;
  @Prop({type: String, required: true}) loginUrl: string | undefined;
  @Prop({type: String, required: true}) loginPageUrl: string | undefined;
  @Prop({type: String, required: true}) registerTicketUrl: string | undefined;
  @Prop({type: String, required: true}) eventUids: string | undefined;
  @Prop({type: String, required: true}) cartPage: string | undefined;

  storageProducts!: Array<Product>;
  storageEvents!: Array<Event>;
  userLanguage!: string;
  sessionDTO: SessionDTO | null | undefined;
  currentBuyerData: CustomerDataDTO | null | undefined;
  currentOrder!: Order | null;
  cart!: CartDTO | null;

  errorCode: string | null = null;
  hasErrors: boolean = false;
  loading: boolean = false;
  codeLoading: boolean = false;
  newUserCode: string = '';
  lastUserCode: string = '';
  newUserCodeError: boolean = false;
  newUserCodeIsPromotion: boolean = false;
  flashingMiniCartClass: string = 'miniCart--flash';
  events: Array<Event> = [];
  products: Array<Product> = [];
  onSiteProducts: Array<Product> = [];
  slotIds: Array<number> = [];
  slots: Array<Slot> = [];
  _productsLookupCache = {};
  availableSubProductIds: Array<number | string> = [];
  eventSelection: Event | null = null;
  ticketConfigurations: Array<TicketConfiguration | TicketConfigurationCollection> = [];
  selectedSlots: Array<number> = [];
  orderDTO: OrderDTO = new OrderDTO();
  openingTimes: Array<OpenPeriod> = [];
  productGroupIds: Array<number> = [];
  productGroups: Array<ProductGroup> = [];
  selectedProductGroup: ProductGroup | null = null;

  productService!: ProductService;
  eventService!: EventService;
  orderService!: OrderService;
  authService!: AuthService;
  slotService!: SlotService;
  openingTimeService!: OpeningTimeService;
  productGroupService!: ProductGroupService;
  paymentMethods!: Array<PaymentMethod>;

  protected paymentMethodService!: PaymentMethodService;

  private paymentProducts: Array<any> = [];
  private privacyStatementService!: PrivacyStatementService;
  private termsAndConditionsService!: TermsAndConditionsService;
  private privacyStatements: any = {};
  private agb: any = {};
  private onSiteProductsMapping: { [key: string]: { sb: Product | null, helpdesk: Product | null } } = {};
  private isFiltered: boolean = false;

  created() {
    this._productsLookupCache = {};
    this.productService = new ProductService(this.inventoryUrl);
    this.eventService = new EventService(this.inventoryUrl);
    this.orderService = new OrderService(this.inventoryUrl);
    this.slotService = new SlotService(this.inventoryUrl);
    this.productGroupService = new ProductGroupService(this.inventoryUrl);
    this.authService = new AuthService(this.loginUrl);
    this.paymentMethodService = new PaymentMethodService(this.inventoryUrl);
    this.privacyStatementService = new PrivacyStatementService(this.inventoryUrl);
    this.termsAndConditionsService = new TermsAndConditionsService(this.inventoryUrl);
  }

  async mounted() {
    this.loading = true;
    if (typeof this.inventoryUrl !== 'string') {
      this.hasErrors = true;
      return;
    }

    if (this.currentOrder) {
      this.orderDTO = this.orderDTO.fromModel(this.currentOrder);
    }
    await this.fetchEvents();
    await this.update();

    this.checkForCodeInUrl();
    this.setAvailablePaymentMethods();
    this.getPrivacyStatements();
  }

  async fetchEvents() {
    const filters: any = {};
    if (typeof this.eventUids !== 'undefined' && this.eventUids !== '') {
      filters.id = `[${this.eventUids}]`;
    }

    this.events = await this.eventService.listCached<Event>('', filters);
    this.eventSelection = this.events[0] || null;
  }

  async fetchProductsForSelectedEvent() {
    if (this.eventSelection !== null) {
      const _subProducts: Array<number | string> = [];

      this.products = await this.productService.listCached<Product>('', {eventId: this.eventSelection.id});
      this.products.sort((p1, p2) => p1.sortOrder > p2.sortOrder ? 1 : -1);
      this.products.forEach((product) => {
        this._productsLookupCache[product.id] = product;
        if (product.parentProduct) {
          _subProducts.push(product.id);
        }
        if (product.slots.length !== 0) {
          product.slots.forEach((slot: number) => {
            this.slotIds.push(slot);
          });
        }
        if (product.productGroups.length !== 0) {
          product.productGroups.forEach((pg: number) => {
            this.productGroupIds.push(pg);
          });
        }
      });
      _subProducts.forEach((subProduct) => {
        if (this._productsLookupCache[subProduct]) {
          this.availableSubProductIds.push(this._productsLookupCache[subProduct].id);
        }
      });
    }
  }

  async fetchOnSiteProductsForSelectedEvent() {
    if (this.eventSelection !== null) {
      this.onSiteProducts = await this.productService.listOnSiteProducts(this.eventSelection.id);
      this.onSiteProducts.sort((p1, p2) => p1.sortOrder > p2.sortOrder ? 1 : -1);
      this.onSiteProducts.forEach((product) => {
        this._productsLookupCache[product.id] = product;
        if (product.slots.length !== 0) {
          product.slots.forEach((slot: number) => {
            this.slotIds.push(slot);
          });
        }
      });
    }
  }

  async fetchSlotsForProducts() {
    if (this.slotIds.length) {
      this.slots = await this.slotService.listCached<Slot>('', {id: JSON.stringify(this.slotIds)});
    }
  }

  async fetchProductGroupsForProducts() {
    if (this.productGroupIds.length) {
      this.productGroups = await this.productGroupService.listCached<ProductGroup>('', {id: JSON.stringify(this.productGroupIds)});
      this.productGroups.sort((a, b) => a.sortingKey - b.sortingKey);
    }
  }

  async update() {
    this.loading = true;
    this.products = [];
    this.onSiteProducts = [];
    this.slots = [];
    this.productGroups = [];
    this.ticketConfigurations = [];
    this.openingTimes = [];
    await this.fetchProductsForSelectedEvent();
    await this.fetchOnSiteProductsForSelectedEvent();
    // await this.updateUrl('');
    this.createOnSiteProductsMapping();
    await this.fetchSlotsForProducts();
    if (this.eventSelection && this.eventSelection.useFilterInTicketlist)
      await this.fetchProductGroupsForProducts();
    this.assembleTicketConfigurations();
    await this.getOpeningTimes();
    this.loading = false;
  }

  // async updateUrl(newUrlValue) {
  //   const urlEventId: string = '#' + this.eventSelection?.eventId;
  //   // @ts-ignore
  //   window.history.pushState({}, '', urlEventId);
  //   const currentUrl = window.location.href;
  //   if (this.isFiltered) {
  //     const newUrl = `${currentUrl}/${newUrlValue}`;
  //     window.history.pushState({}, '', newUrl);
  //   }

  async getPrivacyStatements() {
    if (this.eventSelection) {
      const clientIds = this.events.map(e => e.clientId);
      const statements = await this.privacyStatementService.listCached<PrivacyStatement>('', {clientId: `[${clientIds.join(',')}]`});
      const termsAndConditions = await this.termsAndConditionsService.listCached<TermsAndConditions>(
        '',
        {clientId: `[${clientIds.join(',')}]`},
      );
      this.events.forEach(event => {
        const ps = statements.find(p => p.clientId === event.clientId);
        const tac = termsAndConditions.find(p => p.clientId === event.clientId);
        if (ps) {
          Vue.set(this.privacyStatements, `${event.clientId}`, ps);
        }
        if (tac) {
          Vue.set(this.agb, `${event.clientId}`, tac);
        }
      });
    }
  }

  assembleTicketConfigurations() {
    this.ticketConfigurations = [];
    this.filteredProducts.forEach((product) => {
      if (this.eventSelection !== null) {
        const qrCodeSlots = this.getQrCodeSlotsFor(product);
        if (qrCodeSlots.length !== 0) {
          let ticketConfigurationCollection: TicketConfigurationCollection = new TicketConfigurationCollection();
          qrCodeSlots.forEach((slotId: number) => {
            const slot = this.slotsForQrCode.find(s => s.id === slotId);
            if (slot) {
              const ticketConfiguration = new TicketConfiguration(
                null,
                product,
                this.eventSelection!,
                slot,
                false,
                false,
              );
              const subConfigurations = this.resolveSubTicketConfigurationsForProduct(product, ticketConfiguration);
              ticketConfiguration.registrationRequired = product.registrationRequired;
              ticketConfiguration.subTicketConfigurations = subConfigurations;
              ticketConfigurationCollection.addTicketConfiguration(ticketConfiguration);
            }
          });

          this.ticketConfigurations.push(ticketConfigurationCollection);
        } else {
          const ticketConfiguration = new TicketConfiguration(
            null,
            product,
            this.eventSelection,
            null,
            false,
            false,
          );
          const subConfigurations = this.resolveSubTicketConfigurationsForProduct(product, ticketConfiguration);
          ticketConfiguration.registrationRequired = product.registrationRequired;
          ticketConfiguration.subTicketConfigurations = subConfigurations;
          this.ticketConfigurations.push(ticketConfiguration);
        }
      }
    });

    if (this.cart && this.cart.advertised !== null && this.cart.advertised.length > 0) {
      this.addExistingAdvertisingConfigurations();
    }
  }

  getQrCodeSlotsFor(product: Product) {
    return product.slots.filter((slotId: number) => this.slotsForQrCode.find(s => s.id === slotId) !== undefined);
  }

  getSubProductsFor(product: Product): Array<Product> {
    if (!product.parentProduct) {
      return this.products.filter(p => p.parentProduct === product.id);
    }
    return [];
  }

  createOnSiteProductsMapping() {
    const mapping: { [key: string]: { sb: Product | null, helpdesk: Product | null } } = {};
    this.products.forEach(p => {
      mapping[`${p.id}`] = {
        sb: this.getSBOnSiteProductsFor(p),
        helpdesk: this.getHelpdeskOnSiteProductsFor(p),
      };
    });
    this.onSiteProductsMapping = mapping;
  }

  getOnSiteProductsFor(product: Product) {
    return this.onSiteProducts.filter(p => p.productNumber === product.productNumber);
  }

  getSBOnSiteProductsFor(product: Product) {
    return this.getOnSiteProductsFor(product)
      .find(p => p.distributionChannelId === 4) || null;
  }

  getHelpdeskOnSiteProductsFor(product: Product) {
    return this.getOnSiteProductsFor(product)
      .find(p => p.distributionChannelId === 5) || null;
  }

  resolveSubTicketConfigurationsForProduct(product: Product, parentConfiguration: TicketConfiguration): Array<TicketConfiguration> {
    const subTicketConfigurations: Array<TicketConfiguration> = [];
    const subProducts = this.getSubProductsFor(product);
    subProducts.forEach((subProduct) => {
      if (subProduct.id in this._productsLookupCache) {
        const ticketConfiguration = new TicketConfiguration(
          null,
          subProduct,
          this.eventSelection!,
          null,
          false,
          false,
          parentConfiguration.id,
        );
        subTicketConfigurations.push(ticketConfiguration);
      }
    });
    return subTicketConfigurations;
  }

  decrementQuantity(ticketConfiguration: TicketConfiguration | TicketConfigurationCollection): void {
    if (ticketConfiguration instanceof TicketConfiguration) {
      this.decreaseTicketConfigurationQuantity(ticketConfiguration);
    } else {
      ticketConfiguration.quantity--;
    }
  }

  incrementQuantity(ticketConfiguration: TicketConfiguration | TicketConfigurationCollection): void {
    if (ticketConfiguration instanceof TicketConfiguration) {
      this.increaseTicketConfigurationQuantity(ticketConfiguration);
    } else {
      if (ticketConfiguration.quantity < ticketConfiguration.maxQuantity) {
        ticketConfiguration.quantity++;
      }
    }
  }

  onSlotSelectionModalOpen(ticketConfigurationCollection: TicketConfigurationCollection) {
    this.selectedSlots = [];
    if (ticketConfigurationCollection.quantity > ticketConfigurationCollection.maxQuantity) {
      ticketConfigurationCollection.quantity = ticketConfigurationCollection.maxQuantity;
    }
  }

  selectSlot(ticketConfiguration: TicketConfiguration) {
    ticketConfiguration.quantity++;
  }

  changeSelection($event, index, slotKey) {
    Vue.set(this.selectedSlots, index - 1, slotKey);
  }

  async addToCart(ticketConfiguration: TicketConfiguration) {
    if (ticketConfiguration.quantity > 0) {
      this.loading = true;
      ticketConfiguration.initialQuantity = ticketConfiguration.quantity;
      if (ticketConfiguration.isProductFamily) {
        ticketConfiguration.subTicketConfigurations.forEach(stc => stc.quantity = stc.product.minOrderQuantity);
      }
      const newTicket = (new TicketConfigurationDTO()).fromModel(ticketConfiguration);
      this.orderDTO?.addProduct(newTicket);
      // important after adding cart item to ensure unique ids even after multiple inserts in a row
      ticketConfiguration.resetIds();

      try {
        const cart = await this.orderService.updateCart(this.orderDTO, this.userLanguage);
        await this.$store.dispatch('fetchInitialData', {inventoryUrl: this.inventoryUrl, cart});
        this.$store.commit(UPDATE_CART, cart);
        // due to previous commit this should always be true
        if (this.currentOrder) {
          this.orderDTO = this.orderDTO.fromModel(this.currentOrder);
        }
        await this.fetchSlotsForProducts();
        this.loading = false;
        this.processCartResponse();
        // disable cart fly out on mobile -> LMSTICKET-1343
        if (!this.isMobile) {
          this.flashCartFlyout();
        }
      } catch (e) {
        (new ApiErrorHandler()).handle(e);
        this.orderDTO?.removeConfiguration(ticketConfiguration);
        this.loading = false;
      }
    }
    ticketConfiguration.quantity = 0;
  }

  async addPreselectionToCart(ticketConfigurationCollection: TicketConfigurationCollection) {
    this.loading = true;
    const addedTicketConfs: Array<TicketConfigurationDTO> = [];
    ticketConfigurationCollection.ticketConfigurations.forEach((ticketConfiguration: TicketConfiguration, index: number) => {
      let counter = 0;
      for (let i = 0; i <= ticketConfigurationCollection.quantity; i++) {
        if (this.selectedSlots[i] === index) {
          counter++;
        }
      }
      if (counter > 0) {
        ticketConfiguration.quantity = counter;
        if (ticketConfiguration.isProductFamily) {
          ticketConfiguration.subTicketConfigurations.forEach(stc => stc.quantity = stc.product.minOrderQuantity);
        }
        const newTicket = (new TicketConfigurationDTO()).fromModel(ticketConfiguration);
        this.orderDTO?.addProduct(newTicket);
        addedTicketConfs.push(newTicket);
      }
    });
    // important after adding cart item to ensure unique ids even after multiple inserts in a row
    ticketConfigurationCollection.resetIds();
    try {
      const cart = await this.orderService.updateCart(this.orderDTO, this.userLanguage);
      await this.$store.dispatch('fetchInitialData', {inventoryUrl: this.inventoryUrl, cart});
      this.$store.commit(UPDATE_CART, cart);
      if (this.currentOrder) {
        this.orderDTO = this.orderDTO.fromModel(this.currentOrder);
      }
      this.loading = false;
      this.processCartResponse();
      this.flashCartFlyout();
    } catch (e) {
      (new ApiErrorHandler()).handle(e);
      addedTicketConfs.forEach((addedTicketConf) => this.orderDTO.removeProductByDTO(addedTicketConf));
      this.loading = false;
    }
    this.selectedSlots = [];
    ticketConfigurationCollection.quantity = 0;
  }

  processCartResponse() {
    if (this.cart) {
      if (this.cart.failedSlotUpdates.length === 0) {
        this.showToast(<string>this.$t('toasts.addedToShoppingCart'));
      } else {
        let distinctProducts: any = {};
        this.cart.failedSlotUpdates.forEach((fs) => {
          if (fs.ticketConfig && fs.ticketConfig.product) {
            const prod = fs.ticketConfig.product;
            if (!(prod.id in distinctProducts)) {
              distinctProducts[prod.id] = prod;
            }
          }
        });

        const products: Array<Product> = Object.values(distinctProducts);

        const productTitles: Array<string> = products.map(p => p.descriptionWebShop);
        if (productTitles.length === 0) {
          this.showToast(<string>this.$t('toasts.contingentTicket'));
        } else if (productTitles.length === 1) {
          this.showToast(<string>this.$t('toasts.contingentProduct', {productTitle: productTitles[0]}));
        } else {
          this.showToast(<string>this.$t('toasts.contingentProducts', {productTitle: productTitles.join('", "')}));
        }
      }
    }
  }

  async redeemCode() {
    this.newUserCode = this.newUserCode.toUpperCase();
    this.errorCode = null;
    this.newUserCodeError = false;
    this.newUserCodeIsPromotion = false;
    this.codeLoading = true;
    if (!this.orderDTO.codes.includes(this.newUserCode) && this.newUserCode.length > 0) {
      try {
        const codeResult: CodeResult = await this.orderService.redeemUnclassifiedCode(this.newUserCode);

        if (codeResult.success) {
          switch (codeResult.code.parentName) {
            case 'advertising':
              await this.processAdvertisingCode(codeResult.code);
              break;
            case 'promotion':
              this.lastUserCode = this.newUserCode;
              this.newUserCodeIsPromotion = true;
              break;
            default:
              this.processRegistrationCode(codeResult.code);
          }
        } else {
          if (codeResult.error) {
            this.errorCode = codeResult.error;
          }

          this.lastUserCode = this.newUserCode;
          this.newUserCodeError = true;
        }
      } catch (e) {
        this.lastUserCode = this.newUserCode;
        this.newUserCodeError = true;
      } finally {
        this.codeLoading = false;
      }
    } else {
      this.lastUserCode = this.newUserCode;
      this.newUserCodeError = true;
    }
    this.newUserCode = '';
    this.codeLoading = false;
  }

  async processAdvertisingCode(codeObject: Code) {
    const advertisedProducts = await this.productService.list(codeObject.code);
    let productAdded: boolean = false;
    advertisedProducts.forEach((product: Product) => {
      let relatedEventArray: Array<Event> = this.events.filter((event) => event.id === product.eventId);
      let relatedEvent!: Event;

      if (relatedEventArray.length !== 0 && relatedEventArray[0] instanceof Event) {
        relatedEvent = relatedEventArray[0];
      } else {
        // @TODO: The code is not valid for our configured events, show error
        // return;
      }

      // @TODO: Include product family functionality
      const ticketConfiguration = new TicketConfiguration(null, product, relatedEvent, null, true);
      ticketConfiguration.quantity = 1;
      this.ticketConfigurations.unshift(ticketConfiguration);
      productAdded = true;
    });

    if (productAdded) {
      this.orderDTO.addCode(codeObject.code);
    }
  }

  async getOpeningTimes() {
    const openPeriods: Array<OpenPeriod> = [];
    if (this.eventSelection?.id) {
      this.openingTimeService = new OpeningTimeService(this.inventoryUrl, this.eventSelection?.id);
      let openingTimes = await this.openingTimeService.listCached<OpeningTime>();
      if (openingTimes.length === 0) {
        this.openingTimes = [];
        return;
      }
      let openPeriod = new OpenPeriod(openingTimes[0]);
      openPeriods.push(openPeriod);

      const count = openingTimes.length;
      for (let i = 1; i < count; i++) {
        // in case the times change -> new period starts
        if (
          this.getTime(openPeriod.start) !== this.getTime(openingTimes[i].start)
          || this.getTime(openPeriod.end) !== this.getTime(openingTimes[i].end)
        ) {
          openPeriod = new OpenPeriod(openingTimes[i]);
          openPeriods.push(openPeriod);
        } else {
          // if the time is the same, but the days and months are different -> set sameMonth or/and sameDay false
          if (
            new Date(openPeriod.start).getMonth() !== new Date(openingTimes[i].start).getMonth()
            || new Date(openPeriod.end).getMonth() !== new Date(openingTimes[i].end).getMonth()
          ) {
            openPeriod.sameMonth = false;
          }
          openPeriod.end = openingTimes[i].end;
          openPeriod.sameDay = false;
        }
      }
    }
    this.openingTimes = openPeriods;
  }

  getTime(date) {
    return `${new Date(date).getHours()}:${new Date(date).getMinutes()}`;
  }

  addExistingAdvertisingConfigurations() {
    this.cart!.advertised.forEach(a => {
      const relatedProducts: Array<Product> = this.storageProducts.filter(p => a.products.includes(<number>p.id));
      if (relatedProducts.length > 0) {
        const relatedEvent: Event | undefined = this.storageEvents.find((event) => event.id === relatedProducts[0].eventId);
        if (relatedEvent) {
          relatedProducts.forEach(p => {
            // @TODO: Include product family functionality
            const ticketConfiguration = new TicketConfiguration(null, p, relatedEvent, null, true);
            this.ticketConfigurations.unshift(ticketConfiguration);
          });
        }
      }
    });
  }

  processRegistrationCode(codeObject: Code) {
    this.$store.commit(DELETE_CART);
    this.$store.commit(SET_REGISTRATION_CODES, [codeObject.code]);
    if (this.registerTicketUrl && this.loginPageUrl) {
      if (codeObject.registrationRequired === 'Ja') {
        window.location.href = `${this.loginPageUrl}?returnUrl=${encodeURIComponent(this.registerTicketUrl)}`;
      } else {
        window.location.href = this.registerTicketUrl;
      }
    }
  }

  isConfigurationCollection(objectToIdentify: TicketConfiguration | Array<TicketConfiguration> | TicketConfigurationCollection): boolean {
    return objectToIdentify instanceof TicketConfigurationCollection;
  }

  flashCartFlyout() {
    const cartFlyout = document.querySelector('.js-cart-flyout');

    if (cartFlyout) {
      cartFlyout.classList.add(this.flashingMiniCartClass);
      window.setTimeout(
        () => {
          cartFlyout.classList.remove(this.flashingMiniCartClass);
        },
        2000);
    }
  }

  async setAvailablePaymentMethods() {
    this.paymentMethods = await this.paymentMethodService.listCached<PaymentMethod>('', {active: 1});
    this.$store.commit(UPDATE_PAYMENT_METHODS, this.paymentMethods);
    const paymentMethods = this.paymentMethods;
    // Get only ids-array from active payment-methods
    let activePaymentMethodIds = paymentMethods.map((pm, key) => pm.id);
    // If paymentMethods are empty for the event, all paymentmethods are allowed, so set the active paymentmethods
    let events = this.events;
    events.forEach(function (event) {
      if (!event.paymentMethods || event.paymentMethods.length === 0) {
        // @ts-ignore
        event.paymentMethods = activePaymentMethodIds;
      }
    });

    events.forEach((event) => {
      // Create an intersection for N sets of payment-method-ids
      const intersectedEventPaymentMethodsIds = event.paymentMethods.filter(value => event.paymentMethods.includes(value));
      // Create intersection from event-paymentmethods and active paymentmethods
      // Just in case an inactive paymentmethod is set for an event
      const intersection = intersectedEventPaymentMethodsIds.filter(value => activePaymentMethodIds.includes(value));

      // Return payment-methods form intersectionId-Array
      // @ts-ignore
      let availablePaymentMethods = paymentMethods.filter(pm => intersection.indexOf(pm.id) !== -1);
      if (availablePaymentMethods.length > 0) {
        const availablePayments: Array<string> = [];
        // Display all paymentMethods that are active and set available in events (lowest common denominator)
        availablePaymentMethods.forEach((pm) => {
          availablePayments.push(pm.type);
        });
        this.paymentProducts.push({eventId: event.id, paymentMethods: availablePayments});
      } else {
        // Fallback to credit-card if there are no available payment methods
        this.paymentProducts.push({eventId: event.id, paymentMethods: PaymentTypeEnum.creditcard.value});
      }
    });
  }

  openPrivacyModal() {
    // @ts-ignore
    this.$refs.privacyModal.showModal();
  }

  openAGBModal() {
    // @ts-ignore
    this.$refs.agbModal.showModal();
  }

  getFormattedDate(period) {
    let weekDays;
    let dates;
    // if period has more than one days: display weekdays e.g. Sa - Mo,
    const weekDayStart = new Date(period.start).toLocaleDateString(this.locales, {weekday: 'short'});
    const getDayEnd = new Date(period.end).toLocaleDateString(this.locales, {day: '2-digit'});
    const getMonthEnd = new Date(period.end).toLocaleDateString(this.locales, {month: '2-digit'});
    const getYearEnd = new Date(period.end).toLocaleDateString(this.locales, {year: 'numeric'});
    if (!period.sameDay) {
      const weekDayEnd = new Date(period.end).toLocaleDateString(this.locales, {weekday: 'short'});
      weekDays = `${weekDayStart} - ${weekDayEnd}, `;

      //if period is over more than 1 month: display day and month e.g. 30.03 - 02.04.2023,
      if (!period.sameMonth) {
        const getDayStart = new Date(period.start).toLocaleDateString(this.locales, {day: '2-digit'});
        const getMonthStart = new Date(period.start).toLocaleDateString(this.locales, {month: '2-digit'});
        dates = `${getDayStart}.${getMonthStart} - ${getDayEnd}.${getMonthEnd}.${getYearEnd}, `;

        // if period is over 1 month: display only day e.g. 8. - 11.03.2023,
      } else {
        const isStartDaySingleDigit = new Date(period.start).getDate() < 10;
        if (isStartDaySingleDigit) {
          dates = `0${new Date(period.start).getDate()}. - ${getDayEnd}.${getMonthEnd}.${getYearEnd}, `;
        } else {
          dates = `${new Date(period.start).getDate()}. - ${getDayEnd}.${getMonthEnd}.${getYearEnd}, `;
        }
      }
    } else {
      // if period is over one day: display weekday and date e.g. Sa, 08.03.2023,
      weekDays = `${weekDayStart}, `;
      dates = `${getDayEnd}.${getMonthEnd}.${getYearEnd}, `;
    }
    // display times e.g. 12:00 - 16-00
    const timesStart = new Date(period.start).toLocaleString(this.locales, {hour: '2-digit', minute: '2-digit'});
    const timesEnd = new Date(period.end).toLocaleString(this.locales, {hour: '2-digit', minute: '2-digit'});
    const timeString = this.locales === 'de-de' ? this.$t('ticketList.openingTimes.time') : '';
    let times = `${timesStart} - ${timesEnd} ${timeString}`;
    const formattedTimes = times.replace(/ /g, '&nbsp;');
    return `${weekDays}${dates}${formattedTimes}`;
  }

  updateSelectedProductGroup(productGroup: ProductGroup | null) {
    this.isFiltered = !!productGroup;
    this.selectedProductGroup = productGroup;
    this.assembleTicketConfigurations();
    // this.updateUrl(productGroup?.title);
  }

  get slotsForQrCode() {
    return this.slots.filter(s => s.addToQrCode === 1);
  }

  get filteredProducts(): Array<Product> {
    let products: Array<Product> = [];
    if (this.eventSelection && this.eventSelection.useFilterInTicketlist) {
      this.products.forEach((p) => {
        let id;
        if (typeof p.id === 'string') {
          id = parseInt(p.id, 10);
        } else {
          id = p.id;
        }
        if (this.selectedProductGroup && this.selectedProductGroup.products.includes(id)) {
          products.push(p);
        }
      });
    }
    return products.length > 0 ? products : this.products;
  }

  get itemInShopper(): boolean {
    let productCount: number = 0;
    if (this.currentOrder) {
      this.currentOrder.orderItems.forEach((orderItem: OrderItem) => {
        productCount += orderItem.quantity;
      });
    }
    return productCount > 0;
  }

  get paymentChoices() {
    const choices: Array<{ value: string, text: string, disabled: boolean }> = [];
    if (this.eventSelection) {
      this.paymentProducts.forEach((payment) => {
        if (payment.eventId === this.eventSelection?.id) {
          try {
            payment.paymentMethods.forEach((p) => {
              if (PaymentTypeEnum[p]) {
                choices.push({
                  value: PaymentTypeEnum[p].value,
                  text: PaymentTypeEnum[p].title[this.userLanguage],
                  disabled: false,
                });
              }
            });
          } catch {
            if (PaymentTypeEnum[payment]) {
              choices.push({
                value: PaymentTypeEnum[payment].value,
                text: PaymentTypeEnum[payment].title[this.userLanguage],
                disabled: false,
              });
            }
          }
        }
      });
    }
    return choices;
  }

  get selectedPrivacyStatement() {
    if (this.eventSelection) {
      return this.privacyStatements[this.eventSelection.clientId] || null;
    }
    return null;
  }

  get selectedAGB() {
    if (this.eventSelection) {
      return this.agb[this.eventSelection.clientId] || null;
    }
    return null;
  }

  get locales() {
    return `${this.userLanguage}-${this.userLanguage}`;
  }

  get i18nTermsAndConditionsLink(): string {
    return PublicLinks.TERMS_AND_CONDITIONS[this.userLanguage];
  }

  get i18nPrivacyStatementLink(): string {
    return PublicLinks.PRIVACY_STATEMENT[this.userLanguage];
  }

  get i18nPrivacyStatementContent(): string {
    if (this.selectedPrivacyStatement) {
      let content = this.selectedPrivacyStatement.html;
      if (this.userLanguage !== 'de' && this.selectedPrivacyStatement.translations) {
        const translation = this.selectedPrivacyStatement.translations[this.userLanguage];
        content = translation ? translation.html : content;
      }
      return content;
    }
    return '';
  }

  get i18nTermsAndConditionsContent(): string {
    if (this.selectedAGB) {
      let content = this.selectedAGB.html;
      if (this.userLanguage !== 'de' && this.selectedAGB.translations) {
        const translation = this.selectedAGB.translations[this.userLanguage];
        content = translation ? translation.html : content;
      }
      return content;
    }
    return '';
  }

  @Watch('currentOrder.orderItems')
  updateOrderItems(newValue: Array<OrderItem> | undefined, oldValue: Array<OrderItem>) {
    if (typeof newValue !== 'undefined' && this.currentOrder) {
      this.orderDTO = this.orderDTO.fromModel(this.currentOrder);
    } else {
      this.orderDTO = new OrderDTO();
      this.assembleTicketConfigurations();
    }
  }

  /* reset error when newUserCode updates */
  @Watch('newUserCode')
  setNewUserCode(newValue: string, oldValue: string) {
    if (oldValue !== newValue && newValue !== '') {
      this.newUserCode = newValue.toUpperCase();
      this.newUserCodeError = false;
      this.newUserCodeIsPromotion = false;
    }
  }

  @Watch('eventSelection.eventId')
  changeIsFilteredStatus(newValue: any, oldValue: any) {
    if (newValue !== oldValue) {
      this.isFiltered = false;
    }
  }

  /**
   * If there is a minimum order quantity restriction and the ticket is added to cart,
   * directly jump to the minimum order quantity
   */
  private increaseTicketConfigurationQuantity(ticketConfiguration: TicketConfiguration): void {
    if ((ticketConfiguration.quantity + 1) < ticketConfiguration.product.minOrderQuantity) {
      ticketConfiguration.quantity = ticketConfiguration.product.minOrderQuantity;
    } else {
      ticketConfiguration.quantity++;
    }
  }

  /**
   * If there is a minimum order quantity restriction and the ticket-amount is decreased,
   * set it to zero if the amount is lower than the minimum restriction
   */
  private decreaseTicketConfigurationQuantity(ticketConfiguration: TicketConfiguration): void {
    if ((ticketConfiguration.quantity - 1) < ticketConfiguration.product.minOrderQuantity) {
      ticketConfiguration.quantity = 0;
    } else {
      ticketConfiguration.quantity--;
    }
  }

  private async checkForCodeInUrl() {
    const urlIdentifier = 'ticket-code=';
    let hash = window.location.hash;
    hash = hash.substring(hash.indexOf('#') + 1);
    if (hash.includes(urlIdentifier)) {
      this.newUserCode = hash.substring(hash.indexOf(urlIdentifier) + urlIdentifier.length);
      this.redeemCode();
    }
  }
}
